
/**
 * Purifies the input HTML by removing script, style tags, and empty elements.
 *
 * @param {string} html - The HTML string to be purified.
 * @returns {string} - The purified HTML with script, style tags, and empty elements removed.
 */
import get from 'lodash/get';

export const htmlPurifier = (html) => {

  if (typeof html !== 'string') return '';

  const parser = new DOMParser();
  const doc = parser.parseFromString(html, 'text/html');

  // Remove all script tags
  const scriptTags = doc.querySelectorAll('script');
  scriptTags.forEach((tag) => tag.remove());

  // Remove all style tags
  const styleTags = doc.querySelectorAll('style');
  styleTags.forEach((tag) => tag.remove());

  // Remove all empty tags
  const emptyTags = doc.querySelectorAll('*:empty');
  emptyTags.forEach((tag) => {
    if (tag.tagName.toLowerCase() !== 'br') {
      tag.remove();
    }
  });

  // Get the purified HTML
  const purifiedHtml = get(doc, 'body.innerHTML', '');
  return purifiedHtml;
};


export const normalizeEmptyParagraphs = (html) => {
  if (!html || typeof html !== 'string') {
    return '';
  }

  // Step 1: Replace empty tags (like <p></p>, <h5></h5>, etc.) with <tag><br/></tag>
  let normalized = html.replace(
    /<([a-z][a-z0-9]*)([^>]*)>\s*<\/\1>/gi,
    (match, tagName, attributes) => {
      return `<${tagName}${attributes}><br/></${tagName}>`;
    });

  // Step 2: Remove trailing tags with <br> at the end
  normalized = normalized.replace(
    /(<([a-z][a-z0-9]*)[^>]*><br[^>]*><\/\2>)+$/gi,
    '');

  return normalized;
};

/**
 * Converts RichText HTML to ChatInput-compatible HTML that preserves empty paragraphs.
 * Empty tags are converted to tags with <br/> to prevent browser from collapsing them.
 * This is the inverse of convertChatInputToRichText.
 *
 * @param {string} html - The HTML string from RichText editor.
 * @returns {string} - The HTML string compatible with ChatInput contenteditable.
 */
export const convertRichTextToChatInput = (html) => {
  if (!html || typeof html !== 'string') {
    return '';
  }

  let converted = html;

  // Replace empty tags (like <h5></h5>, <p></p>, <div></div>, etc.) with <tag><br/></tag>
  // This preserves the original tag structure while making empty tags visible in ChatInput
  converted = converted.replace(
    /<([a-z][a-z0-9]*)([^>]*)>\s*<\/\1>/gi,
    (match, tagName, attributes) => {
      return `<${tagName}${attributes}><br/></${tagName}>`;
    });

  // Normalize any existing <tag><br></tag> or <tag><br/></tag> to consistent format
  converted = converted.replace(
    /<([a-z][a-z0-9]*)([^>]*)>\s*<br\s*\/?>\s*<\/\1>/gi,
    (match, tagName, attributes) => {
      return `<${tagName}${attributes}><br/></${tagName}>`;
    });

  return converted;
};

/**
 * Converts ChatInput HTML back to RichText-compatible HTML.
 * Tags containing only <br> are converted back to empty tags, preserving the tag type.
 * This is the inverse of convertRichTextToChatInput.
 *
 * @param {string} html - The HTML string from ChatInput contenteditable.
 * @returns {string} - The HTML string compatible with RichText editor.
 */
export const convertChatInputToRichText = (html) => {
  if (!html || typeof html !== 'string') {
    return '';
  }

  let converted = html;

  // Convert tags that contain only whitespace and <br> tags back to empty tags
  // This preserves the original tag type (e.g., <h5><br></h5> => <h5></h5>)
  // But NOT tags with actual content (e.g., <h5>text<br>more</h5> stays as is)
  converted = converted.replace(
    /<([a-z][a-z0-9]*)([^>]*)>\s*(<br\s*\/?>)*\s*<\/\1>/gi,
    (match, tagName, attributes) => {
      // Convert back to empty tag, preserving tag name and attributes
      return `<${tagName}${attributes}></${tagName}>`;
    });

  return converted;
};